import { Badge, Card, Grid, Group, NumberInput, Paper, Stack, Text } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import React from "react";
import { useParams } from "react-router-dom";
import { GET_INSTAGRAM_BEST_POSTS } from "../../services/instagram_analytics";
import {
    AnalyticsEmptyState,
    InstagramAnalyticsHeader,
    MediaTypeBadge,
    formatNumber,
    normalizeInstagramMetrics,
    useInstagramAccounts,
    useInstagramSync,
} from "./shared";

export default function InstagramAnalyticsBestPostsPage() {
    const { oauthId } = useParams();
    const { accounts, loadingAccounts } = useInstagramAccounts();

    const [loading, setLoading] = React.useState<boolean>(true);
    const [limit, setLimit] = React.useState<number>(10);
    const [rows, setRows] = React.useState<any[]>([]);

    const loadData = React.useCallback(async () => {
        if (!oauthId) return;

        try {
            setLoading(true);
            const response = await GET_INSTAGRAM_BEST_POSTS(oauthId, limit || 10);
            setRows(response?.best_posts || []);
        } catch (err: any) {
            showNotification({ message: err.message, color: "red" });
        } finally {
            setLoading(false);
        }
    }, [oauthId, limit]);

    React.useEffect(() => {
        loadData();
    }, [loadData]);

    const { syncing, triggerSync } = useInstagramSync(oauthId, loadData);

    const isEmpty = !loading && rows.length === 0;

    return (
        <Stack>
            <InstagramAnalyticsHeader
                oauthId={oauthId}
                title="Melhores posts"
                subtitle="Publicações com melhor desempenho por pontuação"
                active="best-posts"
                accounts={accounts}
                loadingAccounts={loadingAccounts}
                syncing={syncing}
                onSync={triggerSync}
            />

            <Paper withBorder p="md">
                <Group align="end">
                    <NumberInput
                        label="Limite"
                        value={limit}
                        onChange={(value) => setLimit(Number(value || 10))}
                        min={1}
                        max={50}
                        w={120}
                    />
                </Group>
            </Paper>

            {isEmpty ? (
                <AnalyticsEmptyState oauthId={oauthId} syncing={syncing} onSync={triggerSync} />
            ) : (
                <Grid>
                    {rows.map((item, index) => {
                        const metrics = normalizeInstagramMetrics(item?.metrics || item?.latest_metrics || item);
                        return (
                            <Grid.Col key={`${item.ig_media_id}-${index}`} md={4}>
                                <Card withBorder h="100%">
                                    <Card.Section>
                                        <img
                                            src={item.media_url}
                                            alt={item.caption || "Publicação"}
                                            style={{ width: "100%", height: 220, objectFit: "cover" }}
                                        />
                                    </Card.Section>

                                    <Group position="apart" mt="sm">
                                        <Badge color="dark">#{index + 1}</Badge>
                                        <MediaTypeBadge type={item.media_type} />
                                    </Group>

                                    <Group position="apart" mt="xs">
                                        <Text size="sm" fw={600} lineClamp={2}>
                                            {item.caption || "Sem legenda"}
                                        </Text>
                                        <Badge color="grape" variant="filled">
                                            Pontuação {Number(item.performance_score || 0).toFixed(1)}
                                        </Badge>
                                    </Group>

                                    <Grid mt="xs">
                                        <Grid.Col span={6}>
                                            <Text size="xs" c="dimmed">Impressões</Text>
                                            <Text fw={600}>{formatNumber(metrics.impressions)}</Text>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Text size="xs" c="dimmed">Curtidas</Text>
                                            <Text fw={600}>{formatNumber(metrics.likes)}</Text>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Text size="xs" c="dimmed">Comentários</Text>
                                            <Text fw={600}>{formatNumber(metrics.comments)}</Text>
                                        </Grid.Col>
                                        <Grid.Col span={6}>
                                            <Text size="xs" c="dimmed">Salvamentos</Text>
                                            <Text fw={600}>{formatNumber(metrics.saves)}</Text>
                                        </Grid.Col>
                                    </Grid>
                                </Card>
                            </Grid.Col>
                        );
                    })}
                </Grid>
            )}
        </Stack>
    );
}
